import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  category: string;
  status: string;
  total_minutes: number;
  sales_count: number;
  type: string;
  provider_id: string;
  provider?: {
    id: string;
    name: string;
    service_name: string | null;
    avatar_url: string | null;
    bio: string | null;
  };
}

interface Purchase {
  id: string;
  product_id: string;
}

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, isLoggedIn } = useApp();
  const [visible, setVisible] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 80);
    if (!id) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("*, provider:provider_id(id, name, service_name, avatar_url, bio)")
        .eq("id", id)
        .eq("type", "video")
        .maybeSingle();
      if (data) setProduct(data as Product);
      setLoading(false);
    };
    load();
  }, [id]);

  // SEO: update meta tags based on product data
  useEffect(() => {
    if (!product) return;
    const providerName = product.provider?.service_name || product.provider?.name || "講師";
    const title = `${product.title} | ${providerName} | TSUGITE`;
    const description = product.description
      ? `${product.description.slice(0, 120)}。${providerName}が教える動画講座。TSUGITEで購入・受講できます。`
      : `${providerName}の動画講座「${product.title}」。TSUGITEで購入・受講できます。`;

    document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("keywords", `${product.title}, ${providerName}, 動画講座, ${product.category || ""}, TSUGITE`);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "product", true);
    setMeta("og:url", `https://tsugite.jp/courses/${id}`, true);
    if (product.thumbnail_url) setMeta("og:image", product.thumbnail_url, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Schema.org Course
    let schemaEl = document.getElementById("schema-course");
    if (!schemaEl) {
      schemaEl = document.createElement("script");
      schemaEl.setAttribute("type", "application/ld+json");
      schemaEl.id = "schema-course";
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": product.title,
      "description": product.description || description,
      "url": `https://tsugite.jp/courses/${id}`,
      "image": product.thumbnail_url || "",
      "provider": {
        "@type": "Person",
        "name": providerName,
      },
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": "JPY",
        "availability": "https://schema.org/InStock",
        "url": `https://tsugite.jp/courses/${id}`,
      },
    });

    return () => {
      document.title = "TSUGITE | 職人・専門家から学ぶプラットフォーム";
      if (schemaEl) schemaEl.remove();
    };
  }, [product, id]);

  useEffect(() => {
    if (!session || !id) return;
    const checkPurchase = async () => {
      const { data } = await supabase
        .from("purchases")
        .select("id, product_id")
        .eq("buyer_id", session.user.id)
        .eq("product_id", id)
        .eq("status", "completed")
        .maybeSingle();
      setAlreadyPurchased(!!(data as Purchase | null));
    };
    checkPurchase();
  }, [session, id]);

  const handlePurchase = async () => {
    if (!isLoggedIn || !session) { navigate("/login"); return; }
    if (!product) return;
    setCheckoutLoading(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({
          productId: product.id,
          productTitle: product.title,
          price: product.price,
          productType: "video",
          providerId: product.provider_id,
          successUrl: `${window.location.origin}/mypage?purchase=success`,
          cancelUrl: window.location.href,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      if (json.url) window.location.href = json.url;
    } catch (err) {
      alert(`エラーが発生しました: ${(err as Error).message}`);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-96 mt-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-teal" />
          <p className="text-sm text-charcoal/40 font-sans mt-3">読み込み中...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <i className="ri-video-off-line text-5xl text-charcoal/20 mb-4" />
          <p className="font-serif text-deep-green text-xl mb-2">講座が見つかりません</p>
          <p className="text-sm text-charcoal/50 font-sans mb-6">この講座は削除されたか存在しない可能性があります。</p>
          <Link to="/courses" className="bg-deep-green text-white px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors">
            講座一覧へ戻る
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const providerName = product.provider?.service_name || product.provider?.name || "講師";
  const durationLabel = product.total_minutes ? `${Math.floor(product.total_minutes / 60)}時間${product.total_minutes % 60}分` : "—";
  const isFree = product.price === 0;

  return (
    <div className={`min-h-screen bg-ivory transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <div className="relative bg-deep-green">
          <div className="absolute inset-0 opacity-20">
            {product.thumbnail_url && <img src={product.thumbnail_url} alt={product.title} className="w-full h-full object-cover object-top" />}
          </div>
          <div className="relative max-w-screen-xl mx-auto px-4 md:px-8 py-10 md:py-16">
            {product.provider_id && (
              <Link to={`/providers/${product.provider_id}`} className="inline-flex items-center gap-1.5 text-white/60 text-xs font-sans hover:text-white mb-4 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line" />{providerName}
              </Link>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-teal text-white text-xs px-3 py-1 rounded-full font-sans">動画講座</span>
                  {product.category && <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-sans">{product.category}</span>}
                  {isFree && <span className="bg-gold text-white text-xs px-3 py-1 rounded-full font-sans font-medium">無料サンプル</span>}
                </div>
                <h1 className="font-serif text-white text-2xl md:text-3xl lg:text-4xl font-light leading-snug mb-4">{product.title}</h1>
                <p className="text-white/70 text-sm font-sans leading-relaxed mb-6 max-w-2xl">{product.description}</p>
                <div className="flex flex-wrap gap-5 text-sm text-white/70 font-sans">
                  {product.total_minutes > 0 && <span className="flex items-center gap-1.5"><i className="ri-time-line text-gold" />総収録時間 {durationLabel}</span>}
                  <span className="flex items-center gap-1.5"><i className="ri-user-line text-gold" />{product.sales_count}人が受講</span>
                </div>
              </div>

              {/* Purchase card — desktop */}
              <div className="hidden lg:block bg-white rounded-2xl p-6">
                <div className="relative h-40 rounded-xl overflow-hidden mb-4 bg-stone-100">
                  {product.thumbnail_url ? (
                    <>
                      <img src={product.thumbnail_url} alt={product.title} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                          <i className="ri-play-fill text-deep-green text-2xl ml-1" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-video-line text-4xl text-charcoal/20" />
                    </div>
                  )}
                </div>
                <p className="font-sans text-3xl text-deep-green font-light mb-1">
                  {isFree ? <span className="text-teal">無料</span> : <>¥{product.price.toLocaleString()}<span className="text-xs text-charcoal/40 ml-1">（税込）</span></>}
                </p>
                {alreadyPurchased ? (
                  <div className="mt-4 p-3 bg-teal/10 rounded-xl border border-teal/20">
                    <p className="text-sm text-teal font-sans flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill" />購入済みです。
                    </p>
                    <Link to="/mypage" className="mt-2 block w-full text-center py-2.5 rounded-full border border-teal text-teal text-sm font-sans hover:bg-teal hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                      マイページで視聴する
                    </Link>
                  </div>
                ) : (
                  <button
                    onClick={handlePurchase}
                    disabled={checkoutLoading}
                    className="w-full py-3.5 rounded-full text-sm font-sans font-medium mt-4 cursor-pointer transition-all duration-300 whitespace-nowrap btn-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {checkoutLoading ? (
                      <><i className="ri-loader-4-line animate-spin" />処理中...</>
                    ) : isFree ? "無料で視聴する →" : "今すぐ購入する →"}
                  </button>
                )}
                <div className="mt-4 flex flex-col gap-2 text-xs text-charcoal/50 font-sans">
                  <span className="flex items-center gap-1.5"><i className="ri-infinity-line text-teal" />購入後は無期限視聴</span>
                  <span className="flex items-center gap-1.5"><i className="ri-smartphone-line text-teal" />スマホ・PC対応</span>
                  {!isFree && <span className="flex items-center gap-1.5"><i className="ri-lock-line text-teal" />Stripe決済（安全）</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile sticky purchase bar */}
        <div className="lg:hidden sticky bottom-0 z-40 bg-white border-t border-stone-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-xs text-charcoal/50 font-sans truncate">{product.title}</p>
              <p className="font-sans text-lg text-deep-green font-medium">
                {isFree ? <span className="text-teal">無料</span> : `¥${product.price.toLocaleString()}`}
              </p>
            </div>
            {alreadyPurchased ? (
              <Link to="/mypage" className="flex-shrink-0 py-3 px-6 rounded-full border border-teal text-teal text-sm font-sans whitespace-nowrap cursor-pointer">
                マイページで視聴
              </Link>
            ) : (
              <button
                onClick={handlePurchase}
                disabled={checkoutLoading}
                className="flex-shrink-0 py-3 px-6 rounded-full btn-primary text-sm font-sans whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {checkoutLoading ? <><i className="ri-loader-4-line animate-spin" />処理中</> : isFree ? "無料で視聴" : "購入する"}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            {product.description && (
              <section className="bg-white rounded-2xl p-6 border border-stone-100">
                <h2 className="font-serif text-deep-green text-xl mb-4">講座について</h2>
                <p className="text-sm text-charcoal/70 font-sans leading-relaxed whitespace-pre-wrap">{product.description}</p>
              </section>
            )}
            {/* Mobile: purchase info */}
            <div className="lg:hidden bg-white rounded-2xl p-5 border border-stone-100">
              <h3 className="font-serif text-deep-green text-base mb-3">この講座に含まれるもの</h3>
              <div className="space-y-2 text-sm text-charcoal/60 font-sans">
                {product.total_minutes > 0 && <div className="flex items-center gap-2"><i className="ri-time-line text-teal" />収録時間 {durationLabel}</div>}
                <div className="flex items-center gap-2"><i className="ri-infinity-line text-teal" />購入後は無期限視聴</div>
                <div className="flex items-center gap-2"><i className="ri-smartphone-line text-teal" />スマホ・PC・タブレット対応</div>
                {!isFree && <div className="flex items-center gap-2"><i className="ri-lock-line text-teal" />安全な決済（Stripe）</div>}
              </div>
            </div>
          </div>
          <div className="space-y-5">
            {/* Mobile purchase card placeholder - actual is sticky bar */}
            {product.provider_id && (
              <Link to={`/providers/${product.provider_id}`} className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-stone-100 hover:border-teal transition-colors cursor-pointer group block">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0">
                  {product.provider?.avatar_url ? (
                    <img src={product.provider.avatar_url} alt={providerName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><i className="ri-user-line text-charcoal/30 text-xl" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-charcoal/40 font-sans">講師</p>
                  <p className="font-serif text-deep-green text-sm font-medium group-hover:text-teal transition-colors">{providerName}</p>
                  <p className="text-xs text-charcoal/50 font-sans">{product.provider?.name}</p>
                </div>
                <i className="ri-arrow-right-line text-charcoal/30 group-hover:text-teal transition-colors" />
              </Link>
            )}
            {/* Desktop purchase info */}
            <div className="hidden lg:block bg-white rounded-2xl p-5 border border-stone-100">
              <h3 className="font-serif text-deep-green text-base mb-3">この講座に含まれるもの</h3>
              <div className="space-y-2 text-sm text-charcoal/60 font-sans">
                {product.total_minutes > 0 && <div className="flex items-center gap-2"><i className="ri-time-line text-teal" />収録時間 {durationLabel}</div>}
                <div className="flex items-center gap-2"><i className="ri-infinity-line text-teal" />購入後は無期限視聴</div>
                <div className="flex items-center gap-2"><i className="ri-smartphone-line text-teal" />スマホ・PC・タブレット対応</div>
                {!isFree && <div className="flex items-center gap-2"><i className="ri-lock-line text-teal" />安全な決済（Stripe）</div>}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Add bottom padding on mobile for sticky bar */}
      <div className="lg:hidden h-24" />
      <Footer />
    </div>
  );
}
